import React, { useState, useMemo, useEffect } from 'react';

/**
 * Country codes with flag emoji and dial code. Nigeria first (primary market), then others.
 * Value stored as full international number (e.g. +2348012345678).
 */
export const COUNTRY_CODES = [
  { code: 'NG', dial: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: 'GH', dial: '+233', flag: '🇬🇭', name: 'Ghana' },
  { code: 'KE', dial: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: 'ZA', dial: '+27', flag: '🇿🇦', name: 'South Africa' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'US', dial: '+1', flag: '🇺🇸', name: 'United States' },
  { code: 'CA', dial: '+1', flag: '🇨🇦', name: 'Canada' },
  { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'India' },
  { code: 'AE', dial: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: 'SA', dial: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: 'EG', dial: '+20', flag: '🇪🇬', name: 'Egypt' },
  { code: 'CM', dial: '+237', flag: '🇨🇲', name: 'Cameroon' },
  { code: 'SN', dial: '+221', flag: '🇸🇳', name: 'Senegal' },
  { code: 'ET', dial: '+251', flag: '🇪🇹', name: 'Ethiopia' },
  { code: 'TZ', dial: '+255', flag: '🇹🇿', name: 'Tanzania' },
  { code: 'UG', dial: '+256', flag: '🇺🇬', name: 'Uganda' },
  { code: 'AU', dial: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: 'DE', dial: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: 'FR', dial: '+33', flag: '🇫🇷', name: 'France' },
  { code: 'OTHER', dial: '+', flag: '🌐', name: 'Other' }
];

/**
 * Parse full international number into country dial and local number.
 * e.g. +2348012345678 -> { dial: '+234', local: '8012345678' }
 */
export function parsePhone(value) {
  if (!value || typeof value !== 'string') return { dial: '+234', local: '' };
  const trimmed = value.trim();
  if (!trimmed) return { dial: '+234', local: '' };
  if (trimmed.startsWith('+')) {
    for (let i = COUNTRY_CODES.length - 1; i >= 0; i--) {
      const { dial } = COUNTRY_CODES[i];
      if (dial !== '+' && trimmed.startsWith(dial)) {
        return { dial, local: trimmed.slice(dial.length).replace(/\D/g, '') };
      }
    }
    const match = trimmed.match(/^(\+\d{1,4})/);
    return { dial: match ? match[1] : '+', local: trimmed.replace(/\D/g, '').slice(match ? match[1].length - 1 : 0) };
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 10 && (digits.startsWith('234') || digits.startsWith('0'))) {
    const local = digits.startsWith('234') ? digits.slice(3) : digits.slice(1);
    return { dial: '+234', local };
  }
  return { dial: '+234', local: digits };
}

/**
 * Combine dial + local into full international number.
 */
export function formatPhone(dial, local) {
  const digits = (local || '').replace(/\D/g, '');
  if (!digits) return '';
  const dialClean = (dial || '+234').replace(/\D/g, '');
  return `+${dialClean}${digits}`;
}

const PhoneInput = ({ value = '', onChange, onBlur, placeholder = 'Phone number (optional)', className = '', id }) => {
  const parsed = useMemo(() => parsePhone(value), [value]);
  const [dial, setDial] = useState(parsed.dial);
  const [local, setLocal] = useState(parsed.local);
  useEffect(() => {
    setDial(parsed.dial);
    setLocal(parsed.local);
  }, [value, parsed.dial, parsed.local]);

  const handleDialChange = (e) => {
    const newDial = e.target.value;
    setDial(newDial);
    onChange(formatPhone(newDial, local));
  };

  const handleLocalChange = (e) => {
    const newLocal = e.target.value.replace(/\D/g, '').slice(0, 15);
    setLocal(newLocal);
    onChange(formatPhone(dial, newLocal));
  };

  return (
    <div className={`flex rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-slate-400/30 focus-within:border-slate-400 ${className}`}>
      <select
        value={dial}
        onChange={handleDialChange}
        className="pl-2 pr-1 py-2 text-sm border-0 bg-slate-50 text-slate-700 focus:ring-0 focus:outline-none min-w-[7rem]"
        aria-label="Country code"
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.code} value={c.dial}>
            {c.flag} {c.dial}
          </option>
        ))}
      </select>
      <input
        id={id}
        type="tel"
        value={local}
        onChange={handleLocalChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className="flex-1 min-w-0 px-3 py-2 text-sm border-0 focus:ring-0 focus:outline-none placeholder:text-slate-400"
        autoComplete="tel-national"
      />
    </div>
  );
};

export default PhoneInput;
